import { useCallback } from 'react'

import {
  dashboardv20240509Client,
  Dashboardv20240509Api
} from '@sequencehq/api/dashboard/v20240509'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { SequenceApiError } from '@sequencehq/api'

import { CustomerContact } from 'CustomerContacts/types'
import { toContactBody } from 'CustomerContacts/utils/toContactBody'

import {
  CustomerContactsApiToInternalAdapterResult,
  customerContactsAdapter
} from '../adapters/customerContacts.adapters'

export type CustomerContactPostBody =
  Dashboardv20240509Api.PostCustomerContact.CustomerContactPostBody

export type CustomerContactPutBody =
  Dashboardv20240509Api.PutCustomerContact.CustomerContactPutBody

export type ApiCustomerContact = {
  id: string
  name: string
  email: string
  purposes: CustomerContact['purposes']
  notificationSubscriptions: CustomerContact['notificationSubscriptions']
  createdAt: string
  updatedAt: string
}

export type CustomerContactPorts = {
  load: () => Promise<CustomerContactsApiToInternalAdapterResult>
  createContact: (body: CustomerContactPostBody) => Promise<ApiCustomerContact>
  updateContact: ({
    contactId,
    body
  }: {
    contactId: string
    body: CustomerContactPutBody
  }) => Promise<ApiCustomerContact>
  archiveContact: (contactId: string) => Promise<ApiCustomerContact>
  updateContacts: (contacts: CustomerContact[]) => Promise<void>
}

type CustomerContactsPortImplementation = () => CustomerContactPorts

type UseCustomerContactsPortImplementation = (props: {
  customerId: string
}) => CustomerContactsPortImplementation

export const useCustomerContactsPortImplementation: UseCustomerContactsPortImplementation =
  ({ customerId }) => {
    const load = useCallback(async () => {
      const response = await dashboardv20240509Client.getCustomerContacts({
        customerId
      })

      if (response.error || !response.data) {
        throw new Error('Unable to load customer contacts')
      }

      return customerContactsAdapter.in(customerId, response.data)
    }, [customerId])

    const createContact = useCallback(
      async (body: CustomerContactPostBody) => {
        const response = await dashboardv20240509Client.postCustomerContact({
          customerId,
          body
        })

        if (response.error || !response.data) {
          const errorResponse = await response.res.json().catch(() => undefined)
          throw {
            statusText: response.res.statusText,
            status: response.res.status,
            response: errorResponse
          } satisfies SequenceApiError
        }

        return response.data
      },
      [customerId]
    )

    const updateContact = useCallback(
      async ({
        contactId,
        body
      }: {
        contactId: string
        body: CustomerContactPutBody
      }) => {
        const response = await dashboardv20240509Client.putCustomerContact({
          customerId,
          contactId,
          body
        })

        if (response.error || !response.data) {
          const errorResponse = await response.res.json().catch(() => undefined)
          throw {
            statusText: response.res.statusText,
            status: response.res.status,
            response: errorResponse
          } satisfies SequenceApiError
        }

        return response.data
      },
      [customerId]
    )

    const archiveContact = useCallback(
      async (contactId: string) => {
        const response =
          await dashboardv20240509Client.postCustomerContactArchive({
            customerId,
            contactId
          })

        if (response.error || !response.data) {
          throw new Error('Unable to archive contact')
        }

        return response.data
      },
      [customerId]
    )

    /**
     * Moving the primary or shipping role touches two contacts at once, so it
     * goes through the customer PUT with the whole roster. That way the old and
     * new holder change together, rather than briefly ending up with two (or
     * no) holders between two contact PUTs.
     */
    const updateContacts = useCallback(
      async (contacts: CustomerContact[]) => {
        const customerResponse = await dashboardv99990101Client.getCustomer({
          id: customerId
        })

        if (customerResponse.error || !customerResponse.data) {
          throw new Error('Unable to load customer')
        }

        const response = await dashboardv99990101Client.putCustomer({
          id: customerId,
          body: {
            ...customerResponse.data,
            contacts: contacts.map(toContactBody)
          }
        })

        if (response.error || !response.data) {
          throw new Error('Unable to update contacts')
        }
      },
      [customerId]
    )

    const portImplementation: CustomerContactsPortImplementation = useCallback(
      () => ({
        load,
        createContact,
        updateContact,
        archiveContact,
        updateContacts
      }),
      [archiveContact, createContact, load, updateContact, updateContacts]
    )

    return portImplementation
  }
